import { gql } from "apollo-angular";
import ID from "../../../types/id";
import Fund from "../../../interfaces/fund";
import OutletReviewStatus from "../../../interfaces/outlet-review-status";
import OutletReviewCategory from "../../../interfaces/outlet-review-category";
import Status from "../../../interfaces/status";
import FundType from "../../../interfaces/fund-type";
import OwnedEntityType from "../../../interfaces/owned-entity-type";
import { Person } from "../../../interfaces/person";
import OutletReviewTracker from "../../../interfaces/outlet-review-tracker";
import { User } from "../../../interfaces/user";


const outletTrackerFields = `
  id
  investor_committee_date
  cohn_reznick_review_status_id
  outlet_review_category_id
  equity{
    from_model
    equity
  }
  outletReviewAnalyst {
    id
    name
  }
  project {
    id
    name
    outletReviewTrackers {
      id
      outlet_review_category_id
      fund {
        id
        name
      }
    }
    projectStatuses(hasStatusSequence:{value:"Closing", operator:EQ, column:NAME}) {
      id
      status {
        id
        name
      }
    }
    pipelineProjects(hasPipeline: {value:"Closing", operator:EQ, column: NAME}) {
      id
      pipelineProjectDates(initial_target: false){
        id
        initial_target
        target
        date
        pipelineMilestone{
          milestone {
            name
          }
        }
      }
    }
    sponsor: participants(hasRole: {column: NAME, value: "Sponsor"}){
      sponsor {
        id
        name
        nickname
        is_repeat
      }
    }
    county{
      name
    }
    state{
      abbreviation
    }
    city
    projectCredits {
      id
      projectCreditOutlets {
        estimated_gross_equity
        fund{
          id
          name
        }
      }
    }
  }
  committee_memo_status_id
  third_party_review_date_completed
  executive_report_distributed`;


type TimelineReviewResult = {
  fund: Fund
  outletReviewStatuses: OutletReviewStatus[]
  outletReviewCategories: OutletReviewCategory[]
  statusesBySequence: Status[]
  fundTypes: FundType[]
  owned_entity_type_by_name: OwnedEntityType
  me: User
}
type TimelineReviewVariables = {
  fund_id: ID
}

export const GET_REVIEW_TIMELINE = gql<TimelineReviewResult, TimelineReviewVariables>`
    query getReviewTimeline($fund_id:ID!){
      me{
        permissionsForResource(resourceId: null, resourceType: OUTLETREVIEWTRACKER){
          _edit
        }
      }
      fund(id: $fund_id){
        id
        name
        close_date
        legal_name
        ic_code
        ein
        certificated_load
        non_certificated_load
        invested_equity
        specified_equity
        fundType {
          id
          name
        }
        ownedEntities {
          id
          name
          ownedEntityAttributes {
            ownedEntityType {
              name
            }
          }
        }
        ltmodels(hasModelType: {column: NAME, value: "UTModel", operator: EQ }, orderBy: {column: CREATED, order: DESC}) {
          id
          modelType {
            id
            name
          }
          kpiset {
            id
            kpiset_detail {
              id
            }
          }
          created
        }
        syndicationsDetails {
          id
          budget_amt
          syndications_budget_year
        }
        fundStatuses {
          id
          status {
            id
            name
          }
        }
        outletReviewTrackers {
            ${outletTrackerFields}
        }
      }
      outletReviewStatuses {
        id
        name
      }
      outletReviewCategories {
        id
        name
        sort_order
      }

      statusesBySequence(sequence_name: "Funds") {
        name
        id
      }
      fundTypes {
        id
        name
      }
      owned_entity_type_by_name(name: "Special Purpose Entity"){
        id
        name
      }
      me {
        projectPermissions: permissionsForResource(resourceType: PROJECT) {
          _edit
          _delete
        }
      }
    }
`;

export type UpdateOutletReviewTrackerInput = {
  outlet_review_category_id?: ID|null,
    outlet_review_analyst_id?: ID|null,
  committee_memo_status_id?: ID|null,
  third_party_review_date_completed?: string|null,
  investor_committee_date?: string|null
  cohn_reznick_review_status_id?: ID|null,
  executive_report_distributed?: boolean,
}

type UpdateOutletReviewTrackerResults = {
  updateOutletReviewTracker: OutletReviewTracker;
}
type UpdateOutletReviewTrackerVariables = {
  id: ID,
  input: UpdateOutletReviewTrackerInput
}
export const UPDATE_OUTLET_REVIEW_TRACKER = gql<UpdateOutletReviewTrackerResults, UpdateOutletReviewTrackerVariables>`
  mutation updateOutletReviewTracker($id: ID!, $input: UpdateOutletReviewTrackerInput) {
    updateOutletReviewTracker(id: $id, input: $input) {
        ${outletTrackerFields}
    }
  }
`;
